import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Icon } from 'react-icons-kit';
import { ic_keyboard_arrow_down } from 'react-icons-kit/md/ic_keyboard_arrow_down';
import { inputClasses } from '../../common/tailwindClassnames';

export type SelectMenuOption = {
    value: string;
    label: string;
    icon?: React.ReactNode;
};

type SelectMenuProps = {
    value: string;
    options: SelectMenuOption[];
    onChange: (value: string) => void;
    disabled?: boolean;
    /** Wrapper width class, e.g. "w-44" or "w-full". */
    className?: string;
    /** Smaller text/padding for inline rows (operator, object-type pickers). */
    compact?: boolean;
    dataTestId?: string;
    ariaLabel?: string;
};

// Keyboard-accessible dropdown used across the webhook builder. Unlike a native <select>
// it opens on Enter (as well as Space / Arrow keys), navigates with Arrow/Home/End, closes
// on Escape, and can render an icon beside each option. The menu is portaled to <body> so it
// isn't clipped by `overflow-hidden` panels. TypeSelect builds on this with type icons.
export const SelectMenu = ({ value, options, onChange, disabled, className = "w-44", compact, dataTestId, ariaLabel }: SelectMenuProps) => {
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
    const btnRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const selected = options.find(o => o.value === value);

    const openMenu = () => {
        if (disabled) return;
        const idx = options.findIndex(o => o.value === value);
        setActiveIndex(idx >= 0 ? idx : 0);
        setOpen(true);
    };

    useLayoutEffect(() => {
        if (!open) return;
        const r = btnRef.current?.getBoundingClientRect();
        if (r) setMenuPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }, [open]);

    // Move DOM focus to the active option so keyboard users see the highlight.
    useEffect(() => {
        if (open) optionRefs.current[activeIndex]?.focus();
    }, [open, activeIndex]);

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            const target = e.target as Node;
            if (btnRef.current?.contains(target) || menuRef.current?.contains(target)) return;
            setOpen(false);
        };
        // Reposition is non-trivial while scrolling; close instead so the menu never detaches.
        const onScrollOrResize = () => setOpen(false);
        document.addEventListener('mousedown', onDocClick);
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, [open]);

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return;
        if (!open) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openMenu();
            }
            return;
        }
        switch (e.key) {
            case 'Escape':
                e.preventDefault();
                setOpen(false);
                btnRef.current?.focus();
                break;
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex(i => (i + 1) % options.length);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(i => (i - 1 + options.length) % options.length);
                break;
            case 'Home':
                e.preventDefault();
                setActiveIndex(0);
                break;
            case 'End':
                e.preventDefault();
                setActiveIndex(options.length - 1);
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                onChange(options[activeIndex].value);
                setOpen(false);
                btnRef.current?.focus();
                break;
            default:
                break;
        }
    };

    return (
        <div className={`relative ${className}`} onKeyDown={onKeyDown}>
            <button
                ref={btnRef}
                type="button"
                disabled={disabled}
                onClick={() => (open ? setOpen(false) : openMenu())}
                className={`${inputClasses.standard} ${compact ? 'text-xs py-1' : 'text-sm'} w-full flex items-center justify-between gap-2 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                data-testid={dataTestId}
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className="flex items-center gap-2 min-w-0">
                    {selected?.icon}
                    <span className="truncate">{selected?.label ?? ''}</span>
                </span>
                <Icon icon={ic_keyboard_arrow_down} size={16} className="text-medium-gray flex-shrink-0" />
            </button>
            {open && menuPos && ReactDOM.createPortal(
                <ul
                    ref={menuRef}
                    role="listbox"
                    style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, width: menuPos.width, zIndex: 60 }}
                    className="bg-white border border-divider-lines rounded shadow-lg overflow-hidden"
                >
                    {options.map((o, i) => (
                        <li key={o.value}>
                            <button
                                ref={el => (optionRefs.current[i] = el)}
                                type="button"
                                role="option"
                                aria-selected={o.value === value}
                                onClick={() => { onChange(o.value); setOpen(false); btnRef.current?.focus(); }}
                                onMouseEnter={() => setActiveIndex(i)}
                                // Highlight the hovered/active option in blue; the currently selected
                                // value gets no persistent highlight (less visual noise, more intuitive).
                                className={`w-full flex items-center gap-2 px-3 py-2 ${compact ? 'text-xs' : 'text-sm'} text-left transition-colors focus:outline-none ${i === activeIndex ? 'bg-light-blue' : ''}`}
                            >
                                {o.icon}
                                <span className="truncate">{o.label}</span>
                            </button>
                        </li>
                    ))}
                </ul>,
                document.body
            )}
        </div>
    );
};
